import React from 'react';
import { StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import Text from '@/components/Typography/Text';
import { PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb } from './landingTokens';

const HIGHLIGHTS = [
  { icon: 'folder-open', text: 'Work with the project folders you choose.' },
  { icon: 'globe', text: 'Use websites and selected Mac apps.' },
  { icon: 'mobile-screen', text: 'Continue projects from your paired phone.' },
];

/** A small illustrative workflow, with the full product tour on /mac. */
function MacWorkspacePreview() {
  const { c, isXS } = useLanding();

  return (
    <View style={{ padding: isXS ? 12 : 24, borderRadius: 24, backgroundColor: c.accentSoft }}>
      <View
        accessibilityLabel="Illustrative Gab AI for Mac workspace"
        style={{ borderRadius: 12, borderWidth: 1, borderColor: c.ruleStrong, backgroundColor: c.cardSolid, overflow: 'hidden', ...(isWeb ? { boxShadow: c.shadow } : {}) }}
      >
        <View style={[styles.titlebar, { backgroundColor: c.panel, borderBottomColor: c.rule }]}>
          <View aria-hidden={isWeb ? true : undefined} style={styles.trafficLights}>
            {['#EC6A5E', '#F4BD4F', '#61C454'].map(color => (
              <View key={color} style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: color }} />
            ))}
          </View>
          <Text weight="medium" style={{ color: c.inkSoft, fontSize: 12 }}>Gab AI for Mac</Text>
          <Icon name="table-columns" size={13} color={c.muted} />
        </View>

        <View style={{ padding: isXS ? 18 : 24, gap: 22 }}>
          <View style={styles.row}>
            <Icon name="folder-open" size={15} color={c.accent} />
            <Text weight="medium" style={{ color: c.ink, fontSize: 13 }}>Website refresh</Text>
          </View>

          <View style={{ padding: 15, borderRadius: 12, backgroundColor: c.panel, borderWidth: 1, borderColor: c.rule }}>
            <Text style={{ color: c.ink, fontSize: 14, lineHeight: 23 }}>
              Turn the notes in this folder into a project brief. Save it here so I can review it.
            </Text>
          </View>

          <View style={{ gap: 13 }}>
            <View style={styles.row}>
              <Logo width={20} height={20} color={c.accent} />
              <Text weight="medium" style={{ color: c.ink, fontSize: 14 }}>Your project brief is ready.</Text>
            </View>
            <Text style={{ color: c.inkSoft, fontSize: 14, lineHeight: 23 }}>
              I brought your goals, audience, and next steps together in one document.
            </Text>
            <View style={[styles.row, { padding: 14, borderRadius: 10, backgroundColor: c.accentWash, borderWidth: 1, borderColor: c.rule }]}>
              <Icon name="file-lines" size={21} color={c.accent} />
              <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
                <Text weight="medium" style={{ color: c.ink, fontSize: 13 }}>project-brief.md</Text>
                <Text style={{ color: c.inkSoft, fontSize: 12 }}>Saved to your Mac folder</Text>
              </View>
              <Icon name="check" size={13} color={c.accent} />
            </View>
          </View>
        </View>
      </View>
      <Text style={{ color: c.inkSoft, fontSize: 12, lineHeight: 18, textAlign: 'center', marginTop: 14 }}>
        Illustrative workflow · You control folder access.
      </Text>
    </View>
  );
}

function LandingMac() {
  const { c, width, isXS, isCompact } = useLanding();
  const headingSize = fluid(width, 34, 48);

  return (
    <Section
      label="Gab AI for Mac"
      style={{ borderTopWidth: 1, borderTopColor: c.rule }}
      contentStyle={{ paddingTop: fluid(width, 48, 80), paddingBottom: fluid(width, 48, 80) }}
    >
      <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'stretch' : 'center', gap: isCompact ? 32 : 64 }}>
        <View style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
          <View style={[styles.row, { marginBottom: 18 }]}>
            <Icon name="desktop" size={16} color={c.accent} />
            <Text weight="medium" style={{ color: c.accent, fontSize: 15 }}>Gab AI for Mac</Text>
          </View>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: headingSize, lineHeight: Math.round(headingSize * 1.1), letterSpacing: -headingSize * 0.035, ...(isWeb ? { whiteSpace: 'pre-line' } : {}) }}>
            {'Work where your\nprojects live.'}
          </Text>
          <Text tag="p" style={{ color: c.inkSoft, fontSize: 17, lineHeight: 28, marginTop: 20, maxWidth: 460 }}>
            The desktop app connects Work to the tools and files already on your Mac, with your existing Gab AI account.
          </Text>
          <View style={{ gap: 14, marginTop: 24, marginBottom: 28 }}>
            {HIGHLIGHTS.map(item => (
              <View key={item.icon} style={styles.row}>
                <Icon name={item.icon} size={15} color={c.accent} />
                <Text style={{ flex: 1, color: c.inkSoft, fontSize: 15, lineHeight: 23 }}>{item.text}</Text>
              </View>
            ))}
          </View>
          <PrimaryCta label="Explore Gab AI for Mac" href="/mac" restrained full={isXS} />
          <Text style={{ color: c.inkSoft, fontSize: 12, lineHeight: 19, marginTop: 14, maxWidth: 390 }}>
            Free to install. Work tasks require a plan with Work access.
          </Text>
        </View>
        <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
          <MacWorkspacePreview />
        </View>
      </View>
    </Section>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  titlebar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
  trafficLights: { flexDirection: 'row', gap: 6 },
});

export default React.memo(LandingMac);
